import type { CSSProperties, ReactNode } from 'react';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import { MODAL_ICONS, type ModalIconKind } from './ModalIcon';
import { MODAL_GLYPH_PROPS } from './modalGlyph';

/**
 * The modal-title glyph: 16px at stroke 1.5, on every surface (modalGlyph.ts). A glyph that must match it outside a
 * title (the dashboard What's New thumbs, the DOM callout) spreads this rather than writing the numbers again.
 */
export { MODAL_GLYPH_PROPS };

export interface ModalTitleProps {
  /** A kind from the shared icon map (`'delete'`), or any Hugeicons glyph. Required unless `mark` is given. */
  icon?: ModalIconKind | IconSvgElement;
  /** An integration's own 24px brand tile, drawn in the icon slot in place of the glyph (the integrations dialogs). */
  mark?: ReactNode;
  as?: 'h1' | 'h2' | 'h3';
  id?: string;
  /** The surface's own title type style (`modal-title`, `oq-upgrade-modal__title`, `rp-modal__title`). */
  className?: string;
  /** `on-dark` for a title on a dark lightbox bar: the glyph takes the title's colour instead of muted grey. */
  tone?: 'default' | 'on-dark';
  /** `stacked` puts a larger icon on its own row above the title, for an introduction card (the feature guide). */
  layout?: 'inline' | 'stacked';
  /** Per-title layout the surface already sets (margin, a reveal animation). Never the icon's size or colour. */
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * A modal title with its icon: the one version on every surface (Emily, 2026-10-25). Layout and colour live in
 * `modalTitle.css`, which the server-rendered review page also inlines, so plain-HTML modals draw the same block.
 * The icon box is one line tall, so the glyph centres on the title's first line, and a wrapped title keeps its icon
 * on line one. The icon is decorative: the heading text names the dialog.
 */
export function ModalTitle({ icon, mark, as: Tag = 'h2', id, className, tone = 'default', layout = 'inline', style, children }: ModalTitleProps) {
  const glyph = typeof icon === 'string' ? MODAL_ICONS[icon] : icon;
  const classes = ['oq-modal-title', mark ? 'oq-modal-title--mark' : '', tone === 'on-dark' ? 'oq-modal-title--on-dark' : '', layout === 'stacked' ? 'oq-modal-title--stacked' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <Tag id={id} className={classes} style={style}>
      <span className="oq-modal-title__icon" aria-hidden="true">
        {mark ?? (glyph && <HugeiconsIcon icon={glyph} {...MODAL_GLYPH_PROPS} color="currentColor"
          data-modal-icon={typeof icon === 'string' ? icon : undefined} />)}
      </span>
      <span className="oq-modal-title__text">{children}</span>
    </Tag>
  );
}
